import React from 'react';
import { Input, Select } from 'antd';
import styled from 'styled-components';

import { IOperator, OperatorStatus } from '../../../types/models/tenancy';

const Wrapper = styled.div`
  .section {
    margin-bottom: 22px;
  }

  .section-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: var(--ancana-font-light);
    margin-bottom: 8px;
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: 12px 16px;
  }

  .field-label {
    font-size: 12px;
    font-weight: 600;
    margin-bottom: 4px;
  }

  .hint {
    font-size: 12px;
    color: var(--ancana-font-light);
    margin: 4px 0 0;
  }
`;

export type TenantDraft = Partial<IOperator>;

interface FieldProps {
  label: string;
  hint?: string;
  children: React.ReactNode;
}

const Field = ({ label, hint, children }: FieldProps) => (
  <div>
    <div className="field-label">{label}</div>
    {children}
    {hint && <p className="hint">{hint}</p>}
  </div>
);

export interface TenantIdentityFieldsProps {
  value: TenantDraft;
  onChange: (draft: TenantDraft) => void;
  /** The slug is the tenant's permanent handle; only settable at creation. */
  isNew?: boolean;
}

/**
 * A tenant's non-visual configuration: who it is, and the addresses and URLs
 * every notification it sends is built from.
 *
 * These used to be hardcoded Ancana literals across the backend — the sender
 * address on every email, the link in every owner invitation. Getting one wrong
 * on a live tenant is not a cosmetic error, so each carries the consequence in
 * its hint rather than a restatement of its own name.
 *
 * When creating, the addresses and URLs may be left blank: a tenant on the
 * shared console (every trial) sends from the platform's address under its own
 * name, has owners reply to its first admin, and has no portal host of its own
 * (`OperatorViewSet._with_shared_defaults`). The hints say so.
 */
const TenantIdentityFields = ({ value, onChange, isNew }: TenantIdentityFieldsProps) => {
  const set = (patch: TenantDraft) => onChange({ ...value, ...patch });

  return (
    <Wrapper>
      <div className="section">
        <div className="section-label">Identity</div>
        <div className="grid">
          <Field label="Name" hint="What staff call this tenant.">
            <Input value={value.name || ''} onChange={e => set({ name: e.target.value })} />
          </Field>
          <Field
            label="Brand name"
            hint={isNew
              ? 'Customer-facing, in emails and the portal. Blank: the name.'
              : 'Customer-facing — appears in emails and the portal.'}
          >
            <Input
              value={value.brand_name || ''}
              onChange={e => set({ brand_name: e.target.value })}
            />
          </Field>
          <Field
            label="Slug"
            hint={isNew
              ? 'Permanent handle. Lowercase, hyphenated.'
              : 'Permanent handle — changing it breaks anything keyed on it.'}
          >
            <Input
              value={value.slug || ''}
              onChange={e => set({ slug: e.target.value })}
              style={{ fontFamily: 'monospace' }}
            />
          </Field>
          <Field
            label="Status"
            hint="Suspending cuts every member's access and drops the tenant from host resolution."
          >
            <Select<OperatorStatus>
              value={value.status || 'active'}
              onChange={status => set({ status })}
              style={{ width: '100%' }}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'suspended', label: 'Suspended' },
              ]}
            />
          </Field>
        </div>
      </div>

      <div className="section">
        <div className="section-label">Communications</div>
        <div className="grid">
          <Field
            label="Sender email"
            hint={isNew
              ? "The From address. Blank: the platform's, under this tenant's name (needs no DNS setup)."
              : 'The From address on everything this tenant sends.'}
          >
            <Input
              value={value.sender_email || ''}
              onChange={e => set({ sender_email: e.target.value })}
              placeholder="noreply@example.com"
            />
          </Field>
          <Field
            label="Support email"
            hint={isNew ? 'Where owners reply. Blank: the first admin.' : 'Where owners are told to reply.'}
          >
            <Input
              value={value.support_email || ''}
              onChange={e => set({ support_email: e.target.value })}
              placeholder="support@example.com"
            />
          </Field>
          <Field
            label="Slack channel"
            hint="Channel in the platform's own workspace for ops notifications about this tenant."
          >
            <Input
              value={value.slack_channel || ''}
              onChange={e => set({ slack_channel: e.target.value })}
              placeholder="#ops-example"
            />
          </Field>
        </div>
      </div>

      <div className="section">
        <div className="section-label">URLs</div>
        <div className="grid">
          <Field
            label="Portal base URL"
            hint={isNew
              ? 'Only for a tenant with its own domain. Blank: the shared Caim console (any trial).'
              : "The owner portal's origin. Also how a request host resolves to this tenant. Blank: the shared Caim console."}
          >
            <Input
              value={value.portal_base_url || ''}
              onChange={e => set({ portal_base_url: e.target.value })}
              placeholder="https://example.com"
            />
          </Field>
          <Field label="Admin base URL" hint="Optional — this tenant's own console origin.">
            <Input
              value={value.admin_base_url || ''}
              onChange={e => set({ admin_base_url: e.target.value })}
              placeholder="https://admin.example.com"
            />
          </Field>
        </div>
      </div>

      <div className="section">
        <div className="section-label">Locale</div>
        <div className="grid">
          <Field label="Default locale" hint="ISO code — the language owners get before choosing.">
            <Input
              value={value.default_locale || ''}
              onChange={e => set({ default_locale: e.target.value })}
              placeholder="es"
            />
          </Field>
          <Field label="Country" hint="Two-letter ISO code.">
            <Input
              value={value.country || ''}
              onChange={e => set({ country: e.target.value })}
              placeholder="MX"
              maxLength={2}
            />
          </Field>
          <Field label="Timezone" hint="IANA name — dates in statements and calendars.">
            <Input
              value={value.timezone || ''}
              onChange={e => set({ timezone: e.target.value })}
              placeholder="America/Mexico_City"
            />
          </Field>
        </div>
      </div>
    </Wrapper>
  );
};

export default TenantIdentityFields;
